import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from 'react'
import { Check, Lock, AlertCircle, PenLine, Loader2, X } from 'lucide-react'
import { formatCalendarDateShort } from '@/lib/patient'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SignatureDialog, type ConfirmedSigner } from '@/components/signature/SignatureDialog'
import { SignatureFieldMenu, type SignatureAction } from '@/components/signature/SignatureFieldMenu'
import {
  AllowedSignatoriesDialog,
  FieldAuditLogDialog,
} from '@/components/signature/SignatureRecordDialogs'
import { useAllowedSignatories, useSubmissionSigners } from '@/hooks/useSigners'
import { useOptionalPermission, PERMISSIONS } from '@/lib/permissions'
import { processFieldKeysOf } from '@/lib/processRunBatch'
import { ProcessRunBatchContext, useProcessRunBatch } from '@/hooks/useProcessRunBatch'
import { alreadySignedInSet, distinctSetPeers } from '@/lib/distinctSigners'
import { cn } from '@/lib/utils'
import type {
  FormDefinition,
  FormSubmission,
  FieldDefinition,
  CgtUser,
  CgtFormType,
  OosConfig,
  TableConfig,
  TableRow,
  SectionWidth,
} from '@/types/cgt'
import {
  CGT_FORM_LABELS,
  MR_FIELD_LABELS,
  isConstantField,
  isMultiFileAttachment,
  valueIsAttachment,
  isSubmissionClosed,
  submissionStatusLabel,
  type MrFieldKey,
} from '@/types/cgt'
import { ReviewArea } from './ReviewArea'
import {
  getSections,
  allFields,
  computeLockedSectionIds,
  computeHiddenFieldIds,
  computeHiddenSectionIds,
  isFieldRequiredNow,
  sectionPrerequisiteIds,
  prerequisiteIds,
  isValueEmpty as isEmptyValue,
  SECTION_COLOR_HEADER_CLASS,
} from '@/lib/formSections'
import { formsApi, type CompletionExtras } from '@/api/formsApi'
import { useOptionSetsByIds } from '@/hooks/useOptionSets'
import { usePatientDetail } from '@/hooks/usePatientDetail'
import { FEATURES } from '@/lib/featureFlags'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import {
  evaluateFormula,
  evaluateGateFormula,
  extractFormulaRefs,
  formatCalculated,
  formulaToHuman,
  recomputeCalculated,
  type DisplayFormat,
  type FormulaContext,
} from '@/lib/formula'
import { visibilitySourceIds } from '@/lib/formSections'
import { isFieldWriteLocked } from '@/lib/fieldLock'
import type { CrossFormResolution, CrossRefState } from '@/lib/crossForm'
import { withoutSkippedDependencies } from '@/lib/skippedSources'
import {
  cellKey,
  ADDED_ROWS_KEY,
  calculatedCellNodes,
  effectiveCellOos,
  readAddedRows,
  tableAggregateValues,
  tableRefLabels,
  type ResolvedCell,
} from '@/lib/tableForm'
import { oosGradingType, oosSupportedType, sameOosValue } from '@/lib/oos'
import { TableAddRow, TableGrid } from './TableGrid'
import { AuditTrailPanel } from './AuditTrailPanel'
import { useAuditTrailInvalidator, useReceivedTestCodes } from '@/hooks/useAuditTrail'
import { shownTestCode, testCodeText } from '@/lib/lis/testCodeLabel'
import { ProcessStepField } from './ProcessStepField'
import { AttachmentControl } from './AttachmentControl'
import { FieldRow } from './FieldRow'
import {
  ATTACHMENT_SLOT_PX,
  FIELD_INDICATOR_PX,
  SYSTEM_LABEL_LAYOUT,
  contentFloorPx,
  gridIsSingleColumn,
  placeField,
  type FieldPlacement,
  type ResolvedLabelLayout,
} from '@/lib/fieldLayout'
import { browserControlMeasure } from '@/lib/controlMeasure'
import { useContainerWidth } from '@/hooks/useContainerWidth'
import { OosNumberField } from './OosNumberField'
import { INERT_OOS_GUARD, OosGuardScope, type OosGuard } from './useOosGuard'
import { ImageField, type PreviewImage } from './ImageField'
import { TableImageCell } from './TableImageCell'
import { AttachmentField, type PreviewListFile } from './AttachmentField'
import { TextBlock } from './TextBlock'
import {
  attachmentListHandlers,
  attachmentSlotHandlers,
  useAttachments,
  type AttachmentSlotState,
} from '@/hooks/useAttachments'

// Section width → span in the 12-col layout grid. Literal strings so Tailwind's
// JIT compiler emits them.
const SECTION_SPAN_CLASS: Record<SectionWidth, string> = {
  full: 'sm:col-span-12',
  half: 'sm:col-span-6',
  third: 'sm:col-span-4',
  quarter: 'sm:col-span-3',
}

const FIELD_SPAN_CLASS: Record<SectionWidth, string> = {
  full: 'sm:col-span-12',
  half: 'sm:col-span-6',
  third: 'sm:col-span-4',
  quarter: 'sm:col-span-3',
}

/** The section field grid's gap (gap-5). */
const SECTION_GRID_GAP_PX = 20

/**
 * A section's field grid. Its own component so it can measure itself: the
 * width a field needs depends on how wide the grid is.
 */
function SectionFieldGrid({
  fields,
  formLayout,
  optionSetLabels,
  locked,
  renderField,
}: {
  fields: FieldDefinition[]
  formLayout: FormDefinition['fieldLayout']
  /** Option set id → its labels, so a dropdown is wide enough for its longest option. */
  optionSetLabels: Map<string, string[]>
  locked: boolean
  renderField: (field: FieldDefinition, placement: FieldPlacement) => ReactNode
}) {
  const { ref, width: containerPx } = useContainerWidth<HTMLDivElement>()
  const singleColumn = gridIsSingleColumn(containerPx, SECTION_GRID_GAP_PX)
  return (
    <div
      ref={ref}
      className={cn(
        'grid grid-cols-1 gap-5 p-4 sm:p-5',
        !singleColumn && 'sm:grid-cols-12',
        locked && 'pointer-events-none',
      )}
    >
      {fields.length === 0 ? (
        <p className='col-span-full text-xs italic text-muted-foreground'>
          No fields in this section.
        </p>
      ) : (
        fields
          // An empty text block renders nothing at all, not even a cell, which
          // would still take a gap in the grid.
          .filter(field => !isConstantField(field.fieldType) || !!field.constantText?.trim())
          .map(field => {
            const placement = placeField(
              {
                type: field.fieldType,
                requested:
                  field.fieldType === 'table'
                    ? 'full'
                    : (field.fieldWidth ?? (field.colSpan === 1 ? 'half' : 'full')),
                labelPosition: field.labelPosition,
                labelWidth: field.labelWidth,
                trailingPx: FIELD_INDICATOR_PX + (field.attachment?.enabled ? ATTACHMENT_SLOT_PX : 0),
                // The help text is the control's placeholder (RenderedField).
                minControlPx: contentFloorPx(
                  field.fieldType,
                  [
                    field.helpText ?? '',
                    ...((field.optionSetId ? optionSetLabels.get(field.optionSetId) : field.options) ?? []),
                  ],
                  browserControlMeasure,
                ),
              },
              formLayout,
              containerPx,
              SECTION_GRID_GAP_PX,
            )
            return (
              // In a one-column grid a 12-column span would add implicit tracks
              // whose gaps overflow the section, so the cell spans the one column.
              <div key={field.id} className={singleColumn ? 'col-span-full' : FIELD_SPAN_CLASS[placement.width]}>
                {renderField(field, placement)}
              </div>
            )
          })
      )}
    </div>
  )
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface FormRendererProps {
  definition: FormDefinition
  submission: FormSubmission
  currentUser: CgtUser
  onSubmissionUpdated: (updated: FormSubmission) => void
  /** Builder preview: render fully interactively but never hit the API. */
  preview?: boolean
  /**
   * Hide the renderer's own name/MRN/status header. Set by hosts that already
   * show that context in their own chrome (the patient procedure page's action bar
   * and banner) so it isn't rendered twice.
   */
  hideHeader?: boolean
  /** No Complete button: a host that only shows the form, such as a procedure preview (z8nrz7fb9k). */
  hideComplete?: boolean
  /**
   * What the header calls `patientMrn`. A kit run keeps its supply id there,
   * so its host passes "Kit" rather than presenting it as a patient's MRN.
   */
  recordLabel?: string
  /**
   * Resolved cross-form references ({handle.fieldId}) for this patient's
   * journey — see lib/crossForm.ts. Namespaced values merge UNDER live values
   * for formula/visibility evaluation; states drive the waiting/warning notes.
   * Hosts without journey context (dialog previews) omit it: cross refs then
   * evaluate as empty, matching the "upstream not filled" behavior.
   */
  crossForm?: CrossFormResolution
  /**
   * IANA timezone TODAY()/NOW() resolve through in calculated formulas and
   * formula gates — the selected tenant's timezone, supplied by the host
   * (mirrors `crossForm`: FormRenderer stays host-agnostic rather than
   * reading TenantProvider directly, so it renders standalone in tests/
   * previews with no tenant context). Defaults to 'UTC' when omitted.
   */
  tenantTimezone?: string
  /**
   * Extra completion gate a host can add on top of the built-in required-field
   * and `completionGate` formula checks (e.g. the patient procedure page's
   * "a product must be created first" prompt). Called right before the
   * completion request fires; returning false aborts silently — the host owns
   * surfacing why (e.g. by opening its own dialog) and can retry via
   * `completeTrigger`. Omitted by every other host, so this is a no-op there.
   */
  beforeComplete?: () => boolean
  /**
   * Bumping this re-invokes Complete Form programmatically (e.g. "Skip" on a
   * host's own gate dialog, which needs to finish completion without a second
   * button click). Ignored on the initial value; only a change re-triggers.
   */
  completeTrigger?: number
  /**
   * Sent with the completion request — e.g. the reason labels do not
   * reconcile, which the host collected in its own gate dialog (z8nrz7dt48).
   */
  completionExtras?: CompletionExtras
}

// ─── Cross-form reference notes ──────────────────────────────────────────────

// Human-readable notes for a set of cross-form refs (a field's formula +
// visibility rule, or a calculated table cell's formula alone): 'waiting'
// while the upstream form is unfilled (hint tone), 'missing-form' /
// 'missing-field' when the reference no longer resolves (warning tone).
// Deduplicated by message.
function crossFormNotesFor(
  refs: string[],
  stateByRef: Record<string, CrossRefState>,
): Array<{ tone: 'hint' | 'warning'; text: string }> {
  const notes = new Map<string, { tone: 'hint' | 'warning'; text: string }>()
  for (const ref of refs) {
    const state = stateByRef[ref]
    if (!state || state.status === 'ok') continue
    if (state.status === 'waiting') {
      notes.set(`w:${state.formName}`, {
        tone: 'hint',
        text: `Waiting for "${state.formName}" to be filled.`,
      })
    } else if (state.status === 'skipped') {
      // Ignored on purpose (z8nrz7fn5r): say so, rather than show an empty value.
      notes.set(`s:${state.formName}`, {
        tone: 'hint',
        text: `"${state.formName}" was skipped, so this does not depend on it.`,
      })
    } else if (state.status === 'missing-form') {
      notes.set(`mf:${state.handle}`, {
        tone: 'warning',
        text: `References form "${state.handle}", which is not part of this patient's journey.`,
      })
    } else {
      notes.set(`mx:${state.formName}:${state.fieldId}`, {
        tone: 'warning',
        text: `References a field that no longer exists on "${state.formName}".`,
      })
    }
  }
  return [...notes.values()]
}

// ─── "Saved" tick indicator ───────────────────────────────────────────────────

function SavedTick() {
  return (
    <span className='inline-flex items-center gap-1 text-xs text-green-600 font-medium animate-in fade-in'>
      <Check className='h-3 w-3' /> Saved
    </span>
  )
}

// ─── Inline table field renderer ─────────────────────────────────────────────

function TableCell({
  resolved,
  value,
  savedValue,
  locked,
  spec,
  cellId,
  submissionId,
  preview,
  onLiveChange,
  onSave,
  onSaveStrict,
}: {
  resolved: ResolvedCell
  value: unknown
  /** The value the server holds, which a blur compares against. */
  savedValue: unknown
  locked: boolean
  /** The cell's specification (see effectiveCellOos), or undefined. */
  spec: OosConfig | undefined
  cellId: string
  submissionId: string
  preview: boolean
  onLiveChange: (value: unknown) => void
  onSave: (value: unknown) => void
  /** Throws on failure; used by cells with a specification. */
  onSaveStrict: (value: unknown, comment?: string) => Promise<void>
}) {
  const [local, setLocal] = useState(value != null ? String(value) : '')
  // The value when the cell got focus. `value` follows every keystroke
  // (onLiveChange feeds it back), so blur must compare against this instead.
  const focusValue = useRef('')

  useEffect(() => {
    setLocal(value != null ? String(value) : '')
  }, [value])

  // Fixed text the author wrote for this cell — read-only, stores nothing.
  if (resolved.type === 'description') {
    return (
      <p className='whitespace-pre-line px-1 text-sm text-muted-foreground'>
        {resolved.constantText}
      </p>
    )
  }

  const renderCell = (guard: OosGuard) => {
    // A cell with a specification (z8nrz7eq4h): flag dot, border colour and the
    // comment prompt, around the same control.
    const wrap = (control: ReactNode, shown: unknown) => {
      if (!spec) return control
      const flagged = !!guard.borderColor && sameOosValue(resolved.type, shown, guard.savedValue)
      return (
        <div>
          <div
            className='flex items-center gap-1 rounded-md border border-transparent'
            style={flagged ? { borderColor: guard.borderColor } : undefined}
          >
            <div className='min-w-0 flex-1'>{control}</div>
            {guard.flag}
            {guard.saving && <Loader2 className='mr-1 h-3 w-3 shrink-0 animate-spin text-muted-foreground' />}
          </div>
          {guard.saveError && <p className='px-1 text-[11px] text-destructive'>{guard.saveError}</p>}
          {guard.dialog}
        </div>
      )
    }

    if (resolved.type === 'checkbox') {
      const shown = guard.pending ? guard.pending.value : value
      return wrap(
        <div className='flex justify-center'>
          <Checkbox
            checked={shown === true}
            disabled={locked}
            onCheckedChange={state => {
              onLiveChange(state === true)
              if (spec) guard.guardSave(state === true)
              else onSave(state === true)
            }}
          />
        </div>,
        shown,
      )
    }

    if (resolved.type === 'dropdown') {
      const shown = guard.pending ? guard.pending.value : value
      return wrap(
        <Select
          value={String(shown ?? '')}
          disabled={locked}
          onValueChange={v => {
            onLiveChange(v)
            if (spec) guard.guardSave(v)
            else onSave(v)
          }}
        >
          <SelectTrigger className='h-8 border-0 shadow-none focus:ring-1'>
            <SelectValue placeholder='—' />
          </SelectTrigger>
          <SelectContent>
            {(resolved.options ?? []).filter(Boolean).map(opt => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>,
        shown,
      )
    }

    const inputType =
      resolved.type === 'number' ? 'number' : resolved.type === 'date' ? 'date' : resolved.type === 'datetime' ? 'datetime-local' : 'text'

    return wrap(
      <Input
        type={inputType}
        value={local}
        disabled={locked}
        className='h-8 border-0 shadow-none focus-visible:ring-1'
        onChange={e => {
          setLocal(e.target.value)
          guard.clearError()
          onLiveChange(e.target.value)
        }}
        onFocus={() => {
          focusValue.current = local
        }}
        onBlur={() => {
          if (spec) guard.guardSave(local)
          // Against the value at focus time: `value` is the live draft, which
          // every keystroke has already set to `local`.
          else if (local !== focusValue.current) onSave(local)
        }}
      />,
      local,
    )
  }

  if (!spec) return renderCell(INERT_OOS_GUARD)
  return (
    <OosGuardScope
      spec={spec}
      fieldType={resolved.type}
      fieldId={cellId}
      savedValue={savedValue}
      submissionId={submissionId}
      preview={preview}
      locked={locked}
      onSave={onSaveStrict}
      // A dropdown or checkbox goes back to the saved value; typed text stays.
      onCancel={
        resolved.type === 'dropdown' || resolved.type === 'checkbox' ? () => onLiveChange(savedValue) : undefined
      }
      compactFlag
    >
      {renderCell}
    </OosGuardScope>
  )
}

interface InlineTableProps {
  field: FieldDefinition
  allValues: Record<string, unknown>
  locked: boolean
  formulaCtx: FormulaContext
  /** Cross-form ref state, so a calculated cell can flag an unresolved reference. */
  crossForm?: CrossFormResolution
  onSave: (key: string, newValue: unknown, oldValue: unknown) => Promise<void>
  /** Throws on failure; used by cells with a specification. */
  onSaveStrict: (key: string, newValue: unknown, comment?: string) => Promise<void>
  onLiveChange: (key: string, value: unknown) => void
  /** The values the server holds, keyed like `allValues`. */
  savedValues: Record<string, unknown>
  submissionId: string
  preview: boolean
  /** Cell key → the LIS test code its value was received with. */
  receivedTestCodes: ReadonlyMap<string, string>
  /**
   * Renders one image cell.
   *
   * A render prop rather than six more props: an image cell needs the
   * attachment list, both mutations, the per-slot busy/error map and the
   * preview-image map, all of which are owned by `FormRenderer` — and none of
   * which this component has any other use for.
   */
  renderImageCell: (key: string, caption: string, locked: boolean) => ReactNode
}

function InlineTableRenderer({
  field,
  allValues,
  locked,
  formulaCtx,
  crossForm,
  onSave,
  onSaveStrict,
  onLiveChange,
  savedValues,
  submissionId,
  preview,
  receivedTestCodes,
  renderImageCell,
}: InlineTableProps) {
  const config = field.tableConfig as TableConfig | undefined

  const addedRows = useMemo(() => readAddedRows(allValues), [allValues])
  const rows: TableRow[] = useMemo(
    () => [...(config?.rows ?? []), ...addedRows],
    [config?.rows, addedRows],
  )

  const handleAddRow = useCallback(
    async (label: string) => {
      const nextAdded: TableRow[] = [...addedRows, { id: crypto.randomUUID(), label }]
      await onSave(ADDED_ROWS_KEY, nextAdded, addedRows)
    },
    [addedRows, onSave],
  )

  if (!config) {
    return (
      <p className='py-4 text-center text-sm text-muted-foreground'>
        This table field has no configuration.
      </p>
    )
  }

  return (
    <div className='space-y-4'>
      <Label className='text-sm font-medium'>{field.label}</Label>
      <TableGrid
        config={config}
        rows={rows}
        receivedTestCodes={receivedTestCodes}
        renderCell={(row, col, key, resolved) => {
          if (resolved.type === 'calculated') {
            const computed = formatCalculated(
              evaluateFormula(resolved.formula, allValues, formulaCtx),
              resolved.displayFormat as DisplayFormat | undefined,
            )
            // Same "waiting"/"broken reference" notes an ordinary
            // calculated field gets (crossFormNotesFor) — just shown
            // as a compact icon+tooltip, since a cell has no room for
            // a second line of text.
            const cellNotes = crossForm
              ? crossFormNotesFor(extractFormulaRefs(resolved.formula ?? ''), crossForm.stateByRef)
              : []
            const note = cellNotes[0]
            return (
              <td
                key={col.id}
                className='border bg-muted/30 px-3 py-2 font-mono text-muted-foreground'
              >
                <span className='inline-flex items-center gap-1'>
                  {computed}
                  {note && (
                    <span title={note.text}>
                      {note.tone === 'warning' ? (
                        <AlertCircle className='h-3.5 w-3.5 shrink-0 text-destructive' />
                      ) : (
                        <Lock className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
                      )}
                    </span>
                  )}
                </span>
              </td>
            )
          }
          if (resolved.type === 'image') {
            return (
              <td key={col.id} className='border p-1 align-middle'>
                {renderImageCell(key, `${col.label || 'Image'} — ${row.label}`, locked)}
              </td>
            )
          }
          return (
            <td key={col.id} className='border p-1'>
              <TableCell
                resolved={resolved}
                value={allValues[key]}
                savedValue={savedValues[key]}
                locked={locked}
                spec={effectiveCellOos(config, key)}
                cellId={key}
                submissionId={submissionId}
                preview={preview}
                onLiveChange={v => onLiveChange(key, v)}
                onSave={v => onSave(key, v, savedValues[key])}
                onSaveStrict={(v, comment) => onSaveStrict(key, v, comment)}
              />
            </td>
          )
        }}
      />

      {config.allowAddRows && !locked && (
        <TableAddRow config={config} rowCount={rows.length} onAdd={handleAddRow} />
      )}
    </div>
  )
}

/** Does this field, or any column or cell of this table, carry a specification? */
function hasOosSpec(field: FieldDefinition): boolean {
  if (field.oos && oosGradingType(field.fieldType, field.displayFormat)) return true
  const config = field.tableConfig
  if (field.fieldType !== 'table' || !config) return false
  return config.columns.some(c => !!c.oos) || Object.values(config.cells ?? {}).some(c => !!c.oos)
}

/**
 * Does this field render a picture the reader is meant to open?
 *
 * True for an Image field, and for a table with at least one image column —
 * only when it actually HAS one, so an ordinary grid of numbers still dims on
 * completion like every other input.
 */
function showsImagery(field: FieldDefinition): boolean {
  if (field.fieldType === 'image') return true
  // Its files are opened from the list, and it draws its own locked state.
  if (isMultiFileAttachment(field.fieldType)) return true
  if (field.fieldType !== 'table') return false
  const config = field.tableConfig as TableConfig | undefined
  if (!config) return false
  // Two ways a cell ends up an image: its column says so, or a per-cell
  // override does. Overrides are keyed by the whole `rowId__colId` cell key, so
  // they are scanned by VALUE — indexing them by column id silently matches
  // nothing, which would leave a single overridden cell dimmed.
  if (config.columns?.some(col => col.columnType === 'image')) return true
  return Object.values(config.cells ?? {}).some(cell => cell?.type === 'image')
}

// ─── Signature field ──────────────────────────────────────────────────────────

/**
 * A signature field with the legacy action menu (z8nrz7dt3z / dt40 / dt45).
 *
 * The restriction is shown on the field itself — "Restricted to QA Review" —
 * so the operator knows who may sign before opening anything, and Apply
 * Signature explains itself when the signed-in user is not one of them
 * (z8nrz7dt45 AC3). The server re-checks all of it when the signature is given.
 */
function SignatureFieldView({
  field,
  value,
  locked,
  error,
  currentUser,
  allValues,
  submissionId,
  preview,
  formulaCtx,
  removeBlocked,
  setPeers,
  onAction,
}: {
  field: FieldDefinition
  value: unknown
  locked: boolean
  error: string | undefined
  currentUser: CgtUser
  allValues: Record<string, unknown>
  submissionId: string
  preview: boolean
  formulaCtx: FormulaContext
  removeBlocked: string | null
  /** The other fields of this field's distinct-signer set (z8nrz7dt44). */
  setPeers: FieldDefinition[]
  onAction: (action: SignatureAction) => void
}) {
  const { can } = useOptionalPermission()
  const signatureValue = value as { signedBy: string; signedAt: string } | undefined
  const isSigned = !!signatureValue?.signedBy
  const restricted = (field.allowedSignerGroupIds?.length ?? 0) > 0
  const inSet = setPeers.length > 0
  // Also read for a distinct-signer field: the server says there when the set
  // cannot be completed (AC8).
  const allowed = useAllowedSignatories(submissionId, field.id, (restricted || inSet) && !preview)
  // Blocked BEFORE any password is asked for (AC4). The server applies the
  // same rule to whoever actually signs.
  const signedPeer = alreadySignedInSet([field, ...setPeers], allValues, field, currentUser.id || null)
  const shortfall = !isSigned ? (allowed.data?.distinctSet?.shortfall ?? null) : null

  const gatePasses =
    !field.signatureGate?.formula ||
    evaluateGateFormula(field.signatureGate.formula, allValues, formulaCtx)
  // Only a positive answer blocks: while the list loads, or if it cannot be
  // read, the server is left to decide.
  const selfOutsideGroups =
    restricted && !!allowed.data && !allowed.data.members.some(m => m.id === currentUser.id)
  const groupNames = allowed.data?.groups.map(g => g.name).join(', ')

  const signBlocked = locked
    ? 'This field is locked'
    : !gatePasses
      ? field.signatureGate?.message || 'This field cannot be signed right now.'
      : null
  const applyBlocked =
    signBlocked ??
    (signedPeer
      ? `You already signed "${signedPeer}" — someone else must sign this (use Sign as)`
      : selfOutsideGroups
        ? `You are not in ${groupNames} — use Sign as`
        : null)

  return (
    <div>
      <SignatureFieldMenu
        fieldLabel={field.label}
        signed={isSigned}
        signBlocked={signBlocked}
        applyBlocked={applyBlocked}
        removeBlocked={removeBlocked}
        canUnsign={!preview && can(PERMISSIONS.signaturesUnsign)}
        offerSignAs={!preview}
        showRecordItems={!preview}
        onAction={onAction}
      >
        <div
          className={cn(
            'rounded-lg border-2 p-4 pr-11 space-y-2',
            isSigned ? 'bg-blue-50 border-blue-200' : 'bg-blue-50 border-blue-300 border-dashed',
          )}
        >
          <Label className='text-sm font-medium'>
            {field.label}
            {field.required && <span className='text-destructive ml-1'>*</span>}
          </Label>
          {restricted && groupNames && (
            <p className='text-xs text-blue-700'>
              Restricted to: <span className='font-semibold'>{groupNames}</span>
            </p>
          )}
          {inSet && (
            <p className='text-xs text-blue-700'>
              Must be signed by someone other than the signer of:{' '}
              <span className='font-semibold'>{setPeers.map(p => p.label).join(', ')}</span>
            </p>
          )}
          {shortfall && <p className='text-xs text-amber-700'>{shortfall}</p>}
          {isSigned ? (
            <div>
              <p className='text-sm font-semibold text-blue-900'>{signatureValue!.signedBy}</p>
              <p className='text-xs text-blue-600 mt-0.5'>
                Signed {new Date(signatureValue!.signedAt).toLocaleString()}
              </p>
              <div className='flex items-center gap-1 mt-2 text-xs text-blue-700'>
                <Lock className='h-3 w-3' /> Fields locked after signature
              </div>
            </div>
          ) : (
            <div className='space-y-2'>
              <Button
                size='sm'
                variant='outline'
                className='border-blue-300 text-blue-700 hover:bg-blue-100'
                disabled={!!applyBlocked}
                aria-label={`Apply signature: ${field.label}`}
                onClick={() => onAction('apply')}
              >
                <PenLine className='h-3.5 w-3.5 mr-1.5' />
                Apply signature
              </Button>
              {!locked && applyBlocked && (
                <p className='text-xs text-amber-700'>{applyBlocked}</p>
              )}
            </div>
          )}
        </div>
      </SignatureFieldMenu>
      {error && <p className='text-xs text-destructive mt-1'>{error}</p>}
    </div>
  )
}

// ─── Single field renderer ────────────────────────────────────────────────────

interface FieldProps {
  field: FieldDefinition
  value: unknown
  locked: boolean
  error: string | undefined
  currentUser: CgtUser
  allValues: Record<string, unknown>
  fieldLabels: Record<string, string>
  /** Pre-resolved dropdown options (from an Option Set) — falls back to field.options. */
  resolvedOptions?: string[]
  /** Resolved patient value for an MR field — undefined outside a procedure context. */
  mrValue?: string
  /** The submission instance — process-step executions are keyed to it. */
  submissionId: string
  /** Builder preview: render fully interactively but never hit the API. */
  preview: boolean
  /** now/timezone for TODAY()/NOW() in calculated formulas and formula gates. */
  formulaCtx: FormulaContext
  onSave: (fieldId: string, newValue: unknown, oldValue: unknown) => Promise<void>
  /** Throws on failure; used by number fields with a specification. */
  onSaveStrict: (fieldId: string, newValue: unknown, comment?: string) => Promise<void>
  onLiveChange: (fieldId: string, value: unknown) => void
  onSignatureAction: (fieldId: string, action: SignatureAction) => void
  /** Why a signature on this form cannot be removed right now, or null. */
  signatureRemoveBlocked: string | null
  /** Signature fields only: the other fields of its distinct-signer set. */
  signatureSetPeers: FieldDefinition[]
  /** A process step transitioned — the submission may have changed server-side. */
  onProcessTransitioned: () => void
  /** Where the label sits (z8nrz7djyx); see lib/fieldLayout. */
  layout: ResolvedLabelLayout
  /** The control would be too narrow beside its label, so the label goes above. */
  stacked: boolean
}

function RenderedField({
  field,
  value,
  locked,
  error,
  currentUser,
  allValues,
  fieldLabels,
  resolvedOptions,
  mrValue,
  submissionId,
  preview,
  formulaCtx,
  onSave,
  onSaveStrict,
  onLiveChange,
  onSignatureAction,
  signatureRemoveBlocked,
  signatureSetPeers,
  onProcessTransitioned,
  layout,
  stacked,
}: FieldProps) {
  const patientsOn = useFeatureFlag(FEATURES.patients)
  const [localValue, setLocalValue] = useState<string>(
    value !== undefined && value !== null ? String(value) : '',
  )
  const [saving, setSaving] = useState(false)
  const [showSaved, setShowSaved] = useState(false)
  const prevValueRef = useRef<unknown>(value)
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keep local value in sync when submission values change externally
  useEffect(() => {
    if (!saving) {
      setLocalValue(value !== undefined && value !== null ? String(value) : '')
      prevValueRef.current = value
    }
  }, [value, saving])

  // A text, textarea, dropdown, checkbox or date field with a specification
  // (z8nrz7eq4h) saves through the guard: flag, comment prompt, review. Number
  // fields with one render OosNumberField below instead.
  const oosGuarded = field.fieldType !== 'number' && !!field.oos && oosSupportedType(field.fieldType)
  const handleBlur = useCallback(async () => {
    if (locked) return
    if (localValue === String(prevValueRef.current ?? '')) return
    setSaving(true)
    await onSave(field.id, localValue, prevValueRef.current)
    prevValueRef.current = localValue
    setSaving(false)
    setShowSaved(true)
    if (savedTimer.current) clearTimeout(savedTimer.current)
    savedTimer.current = setTimeout(() => setShowSaved(false), 2000)
  }, [field.id, localValue, locked, onSave])

  // Section — layout divider, no value
  if (field.fieldType === 'section') {
    return (
      <div className='pt-1 pb-0.5'>
        <h3 className='text-sm font-semibold'>{field.label}</h3>
        <Separator className='mt-1.5' />
      </div>
    )
  }

  // Text block (z8nrz7djyy) — fixed text the author wrote. Read-only, and
  // deliberately not an input: there is nothing here for the filler to change,
  // and nothing is stored against it. No label column and no asterisk — the
  // text spans the field's width, with the author's line breaks kept. The
  // label is the author's name for the block in the builder, not form content.
  if (isConstantField(field.fieldType)) {
    if (!field.constantText?.trim()) return null
    return <TextBlock text={field.constantText} />
  }

  // MR field — read-only, populated from the patient record (never from
  // submission.values). Empty when no patient context is available.
  //
  // With the `patients` feature off the lookup 403s, which lands in exactly the
  // same "no patient" bucket as a 404 — so the field would render blank with no
  // explanation. Nothing is persisted either way (this value never reaches
  // submission.values), so the fix is to say why the box is empty.
  if (field.isMrField) {
    return (
      <FieldRow
        label={field.label}
        helpText={field.helpText}
        layout={layout}
        stacked={stacked}
        controlClassName='flex flex-1 flex-col gap-0.5'
      >
        {({ id }) => (
          <>
            <Input
              id={id}
              readOnly
              value={patientsOn ? (mrValue ?? '') : ''}
              placeholder={patientsOn ? '—' : 'Unavailable'}
              className='flex-1 bg-muted text-muted-foreground cursor-default'
            />
            <p className='text-xs text-muted-foreground'>
              {patientsOn ? (
                <>
                  Populated from the patient record
                  {field.mrFieldKey ? ` — ${MR_FIELD_LABELS[field.mrFieldKey]}` : ''}
                </>
              ) : (
                'Patients is disabled for this tenant, so patient details cannot be shown.'
              )}
            </p>
          </>
        )}
      </FieldRow>
    )
  }

  // Process field — Start/End/Complete execution row with e-signed transitions
  // (status + actuals live server-side, not in the submission's values map).
  if (field.fieldType === 'process') {
    return (
      <ProcessStepField
        field={field}
        submissionId={submissionId}
        locked={locked}
        preview={preview}
        onTransitioned={onProcessTransitioned}
      />
    )
  }

  // Signature field — value is { signedBy, signedAt } when applied, stamped by
  // the server from whoever's password signed it.
  if (field.fieldType === 'signature') {
    return (
      <SignatureFieldView
        field={field}
        value={value}
        locked={locked}
        error={error}
        currentUser={currentUser}
        allValues={allValues}
        submissionId={submissionId}
        preview={preview}
        formulaCtx={formulaCtx}
        removeBlocked={signatureRemoveBlocked}
        setPeers={signatureSetPeers}
        onAction={action => onSignatureAction(field.id, action)}
      />
    )
  }

  // Calculated field — read-only, formula-derived, live-updating
  if (field.fieldType === 'calculated') {
    const raw = evaluateFormula(field.formula, allValues, formulaCtx)
    const computed = formatCalculated(raw, field.displayFormat as DisplayFormat | undefined)
    const summary = field.formula ? formulaToHuman(field.formula, fieldLabels) : ''
    // A calculated field with a specification (z8nrz7eq4e) is graded as its
    // result. The server raises the alert when a save changes the result; the
    // field shows the flag and review popover. It never asks for a comment;
    // one can be added from the flag while the form is open.
    const grading = field.oos ? oosGradingType('calculated', field.displayFormat) : null
    const body = (guard: OosGuard) => (
      <FieldRow
        label={field.label}
        helpText={field.helpText}
        layout={layout}
        stacked={stacked}
        controlClassName='flex flex-1 flex-col gap-0.5'
      >
        {({ id }) => (
          <>
            <div className='flex items-center gap-2'>
              <Input
                id={id}
                readOnly
                value={computed}
                style={guard.borderColor ? { borderColor: guard.borderColor } : undefined}
                className='flex-1 bg-muted text-muted-foreground font-mono cursor-default'
              />
              {guard.flag}
            </div>
            {summary && <p className='text-xs text-muted-foreground'>= {summary}</p>}
          </>
        )}
      </FieldRow>
    )
    if (!field.oos || !grading) return body(INERT_OOS_GUARD)
    return (
      <OosGuardScope
        spec={field.oos}
        fieldType={grading}
        fieldId={field.id}
        savedValue={raw}
        submissionId={submissionId}
        preview={preview}
        locked
        commentable={!locked}
        onSave={async () => {}}
      >
        {body}
      </OosGuardScope>
    )
  }

  // The value-entry controls. A field with a specification renders them inside
  // OosGuardScope; one without gets the inert guard and never mounts it.
  const renderInput = (guard: OosGuard) => {
    const oosBorder =
      guard.borderColor && sameOosValue(field.fieldType, localValue, guard.savedValue)
        ? { borderColor: guard.borderColor }
        : undefined
    const busy = saving || guard.saving
    const saved = showSaved || guard.showSaved
    const shownError = guard.saveError ?? error

    // Checkbox (boolean)
    if (field.fieldType === 'checkbox') {
      const checked = (guard.pending ? guard.pending.value : value) === true
      const onCheckedChange = async (state: boolean | 'indeterminate') => {
        const newValue = state === true
        if (locked) return
        onLiveChange(field.id, newValue)
        if (oosGuarded) return guard.guardSave(newValue)
        setSaving(true)
        await onSave(field.id, newValue, prevValueRef.current)
        prevValueRef.current = newValue
        setSaving(false)
        setShowSaved(true)
        if (savedTimer.current) clearTimeout(savedTimer.current)
        savedTimer.current = setTimeout(() => setShowSaved(false), 2000)
      }
      // A configured checkbox takes the label column like any other field; a
      // legacy one keeps its inline label, box first.
      if (!layout.legacy || stacked) {
        const side = !stacked && layout.position !== 'above'
        return (
          <div>
            <FieldRow
              label={field.label}
              required={field.required}
              helpText={field.helpText}
              layout={layout}
              stacked={stacked}
              error={shownError}
              // As tall as an input beside a side label, so the box centres on the
              // label's line.
              controlClassName={cn('flex flex-1 items-center gap-2', side && 'sm:min-h-10')}
            >
              {({ id, describedBy }) => (
                <>
                  <Checkbox
                    id={id}
                    checked={checked}
                    disabled={locked}
                    aria-describedby={describedBy}
                    onCheckedChange={onCheckedChange}
                  />
                  {guard.flag}
                  {busy && <Loader2 className='h-3 w-3 animate-spin text-muted-foreground' />}
                  {saved && <SavedTick />}
                </>
              )}
            </FieldRow>
            {guard.dialog}
          </div>
        )
      }
      return (
        <div className='space-y-1.5'>
          <div className='flex items-start gap-2'>
            <Checkbox
              id={field.id}
              checked={checked}
              disabled={locked}
              className='mt-0.5'
              onCheckedChange={onCheckedChange}
            />
            <div className='flex-1 space-y-1'>
              <div className='flex items-center justify-between gap-2'>
                <Label htmlFor={field.id} className='text-sm font-normal cursor-pointer'>
                  {field.label}
                  {field.required && <span className='text-destructive ml-1'>*</span>}
                </Label>
                <div className='flex items-center gap-2 shrink-0'>
                  {guard.flag}
                  {busy && <Loader2 className='h-3 w-3 animate-spin text-muted-foreground' />}
                  {saved && <SavedTick />}
                </div>
              </div>
              {field.helpText && <p className='text-xs text-muted-foreground'>{field.helpText}</p>}
            </div>
          </div>
          {shownError && <p className='text-xs text-destructive'>{shownError}</p>}
          {guard.dialog}
        </div>
      )
    }

    // Textarea
    if (field.fieldType === 'textarea') {
      return (
        <div>
          <FieldRow
            label={field.label}
            required={field.required}
            helpText={field.helpText}
            layout={layout}
            stacked={stacked}
            tall
            error={shownError}
            controlClassName='flex flex-1 items-start gap-2'
          >
            {({ id, describedBy }) => (
              <>
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  value={localValue}
                  disabled={locked}
                  rows={4}
                  placeholder={field.helpText}
                  className={cn('flex-1', locked && 'opacity-50 cursor-not-allowed bg-gray-50')}
                  style={oosBorder}
                  onChange={e => {
                    setLocalValue(e.target.value)
                    guard.clearError()
                    onLiveChange(field.id, e.target.value)
                  }}
                  onBlur={oosGuarded ? () => guard.guardSave(localValue) : handleBlur}
                />
                <div className='flex items-center gap-1 shrink-0 pt-2'>
                  {guard.flag}
                  {busy && <Loader2 className='h-3 w-3 animate-spin text-muted-foreground' />}
                  {saved && <SavedTick />}
                </div>
              </>
            )}
          </FieldRow>
          {guard.dialog}
        </div>
      )
    }

    // Dropdown
    if (field.fieldType === 'dropdown') {
      return (
        <div>
          <FieldRow
            label={field.label}
            required={field.required}
            helpText={field.helpText}
            layout={layout}
            stacked={stacked}
            error={shownError}
            controlClassName='flex flex-1 items-center gap-2'
          >
            {({ id, describedBy }) => (
              <>
                <Select
                  value={String((guard.pending ? guard.pending.value : value) ?? '')}
                  disabled={locked}
                  onValueChange={async v => {
                    const oldVal = value
                    onLiveChange(field.id, v)
                    if (oosGuarded) return guard.guardSave(v)
                    await onSave(field.id, v, oldVal)
                    setShowSaved(true)
                    if (savedTimer.current) clearTimeout(savedTimer.current)
                    savedTimer.current = setTimeout(() => setShowSaved(false), 2000)
                  }}
                >
                  <SelectTrigger
                    id={id}
                    aria-describedby={describedBy}
                    className={cn('flex-1', locked && 'opacity-50 cursor-not-allowed')}
                    style={!guard.pending ? oosBorder : undefined}
                  >
                    <SelectValue placeholder={field.helpText || 'Select…'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(resolvedOptions ?? field.options ?? []).map(opt => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {guard.flag}
                {guard.saving && <Loader2 className='h-3 w-3 animate-spin text-muted-foreground' />}
                {saved && <SavedTick />}
              </>
            )}
          </FieldRow>
          {guard.dialog}
        </div>
      )
    }

    // Number with a specification (z8nrz7eq4e): flag, comment prompt, review.
    if (field.fieldType === 'number' && field.oos) {
      return (
        <OosNumberField
          field={field}
          value={value}
          locked={locked}
          error={error}
          submissionId={submissionId}
          preview={preview}
          onLiveChange={onLiveChange}
          onSave={onSaveStrict}
          layout={layout}
          stacked={stacked}
        />
      )
    }

    // Text / number / date / datetime
    const inputType =
      field.fieldType === 'number' ? 'number' : field.fieldType === 'date' ? 'date' : field.fieldType === 'datetime' ? 'datetime-local' : 'text'

    return (
      <div>
        <FieldRow
          label={field.label}
          required={field.required}
          helpText={field.helpText}
          layout={layout}
          stacked={stacked}
          error={shownError}
          controlClassName='flex flex-1 items-center gap-2'
        >
          {({ id, describedBy }) => (
            <>
              <Input
                id={id}
                aria-describedby={describedBy}
                type={inputType}
                value={localValue}
                disabled={locked}
                placeholder={field.helpText}
                className={cn('flex-1', locked && 'opacity-50 cursor-not-allowed bg-gray-50')}
                style={oosBorder}
                onChange={e => {
                  setLocalValue(e.target.value)
                  guard.clearError()
                  onLiveChange(field.id, e.target.value)
                }}
                // A guarded date saves on blur too, like every date field: saving on
                // change would grade the half-typed years a date input reports
                // while the year is being typed.
                onBlur={oosGuarded ? () => guard.guardSave(localValue) : handleBlur}
              />
              {guard.flag}
              {busy && <Loader2 className='h-3 w-3 animate-spin text-muted-foreground' />}
              {saved && <SavedTick />}
            </>
          )}
        </FieldRow>
        {guard.dialog}
      </div>
    )
  }

  if (!oosGuarded) return renderInput(INERT_OOS_GUARD)
  return (
    <OosGuardScope
      spec={field.oos}
      fieldType={field.fieldType}
      fieldId={field.id}
      savedValue={value}
      submissionId={submissionId}
      preview={preview}
      locked={locked}
      onSave={(v, comment) => onSaveStrict(field.id, v, comment)}
      // A dropdown or checkbox goes back to the saved value; typed text stays.
      onCancel={
        field.fieldType === 'dropdown' || field.fieldType === 'checkbox'
          ? () => onLiveChange(field.id, value)
          : undefined
      }
    >
      {renderInput}
    </OosGuardScope>
  )
}

// The emptiness predicate + section-completeness rules live in lib/formSections
// (imported above as isEmptyValue / computeLockedSectionIds) so they can be unit
// tested and stay the single source of truth across builder and renderer.

// ─── Main renderer ────────────────────────────────────────────────────────────

export function FormRenderer({
  definition: authoredDefinition,
  submission,
  currentUser,
  onSubmissionUpdated,
  preview = false,
  hideHeader = false,
  hideComplete = false,
  recordLabel = 'Patient MRN',
  crossForm,
  tenantTimezone,
  beforeComplete,
  completeTrigger,
  completionExtras,
}: FormRendererProps) {
  // The form as it behaves in this run: any visibility rule or gate that reads
  // a SKIPPED form is ignored (z8nrz7fn5r), as the server ignores it. Everything
  // below reads this, so no rule needs to know about skipping.
  const skippedKey = (crossForm?.skippedHandles ?? []).join('\u0000')
  const definition = useMemo(
    () => withoutSkippedDependencies(authoredDefinition, new Set(skippedKey ? skippedKey.split('\u0000') : [])),
    [authoredDefinition, skippedKey],
  )
  const [localSubmission, setLocalSubmission] = useState<FormSubmission>(submission)
  // Draft values that update on every keystroke so calculated fields recompute
  // live, before the operand is persisted. Server values (incl. authoritative
  // calculated results) are merged in whenever the submission updates.
  const [liveValues, setLiveValues] = useState<Record<string, unknown>>(submission.values)
  const [lockedFieldIds, setLockedFieldIds] = useState<Set<string>>(new Set())
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  // Per-field upload/remove state — keyed by fieldId rather than a single
  // scalar, so clicking "attach" on field B while field A's own upload is
  // still in flight doesn't clear A's busy indicator (or, worse, have A's
  // later settle wipe B's). A failed mutation keeps its error visible instead
  // of silently going quiet once busy clears.
  const [attachmentFieldState, setAttachmentFieldState] = useState<AttachmentSlotState>({})
  // Preview mode has no persisted submission to attach to, so a picked file is
  // tracked here instead — lifted out of AttachmentControl so handleComplete's
  // required-attachment check (below) can see a preview selection too.
  const [previewAttachments, setPreviewAttachments] = useState<
    Record<string, { fileName: string; sizeBytes: number }>
  >({})
  // Image fields keep their own preview map: the attachment control only needs a
  // name and a size to render its icon, while an image has to actually paint the
  // picture, which means holding the object URL of the file just picked.
  const [previewImages, setPreviewImages] = useState<Record<string, PreviewImage>>({})
  // Attachment fields (z8nrz7dp7p) list several files, so theirs is a list.
  const [previewFileLists, setPreviewFileLists] = useState<Record<string, PreviewListFile[]>>({})
  const setPreviewImage = useCallback((key: string, image: PreviewImage | null) => {
    setPreviewImages(prev => {
      const next = { ...prev }
      // The outgoing blob is released here rather than in the cell: this map is
      // what owns it, and an author trying three pictures in a row should not
      // leave two of them pinned in memory.
      const outgoing = prev[key]
      if (outgoing && outgoing.objectUrl !== image?.objectUrl) {
        URL.revokeObjectURL(outgoing.objectUrl)
      }
      if (image) next[key] = image
      else delete next[key]
      return next
    })
  }, [])
  // Skipped entirely in preview mode: there is no persisted submission to
  // attach to, so a fetch would 404 on a throwaway id.
  const attachments = useAttachments(preview ? undefined : localSubmission.id)
  const attachmentsByFieldId = attachments.byFieldId
  // Field saves bypass TanStack, so the trail has to be told about them; see
  // the cache-wide rule in QueryProvider for everything that does not.
  const invalidateAuditTrail = useAuditTrailInvalidator()
  // The LIS test code each value was received with (z8nrz7g2wm), for the label
  // beneath a field and a cell's hover.
  const receivedTestCodes = useReceivedTestCodes(preview ? null : localSubmission.id)
  const [gateBanners, setGateBanners] = useState<CgtFormType[]>([])
  const [completing, setCompleting] = useState(false)
  const [completionError, setCompletionError] = useState<string | null>(null)

  // Recomputed every render (not memoized) so a gate re-evaluates as real
  // time passes, not just when liveValues change.
  const formulaCtx: FormulaContext = { now: new Date(), timeZone: tenantTimezone ?? 'UTC' }

  // Every Process field's connection, Assign To roster and live holds, read
  // once for the form instead of once per field (z8nrz7e8kw). The fields wait
  // for it through ProcessRunBatchContext, below.
  const processPermission = useOptionalPermission()
  const processFieldIds = useMemo(() => processFieldKeysOf(definition), [definition])
  const { ready: processBatchReady } = useProcessRunBatch({
    fieldIds: processFieldIds,
    submissionId: preview ? null : localSubmission.id,
    canExecute: processPermission.can(PERMISSIONS.processExecute),
    enabled: !preview,
  })
  const processBatchContext = useMemo(() => ({ ready: processBatchReady }), [processBatchReady])

  // Collect the distinct option-set ids referenced by this form's fields so we
  // fetch ONLY those sets (by id) rather than the first page of all sets.
  // This guarantees resolution regardless of how many total option sets exist.
  const referencedOptionSetIds = useMemo(() => {
    const ids = new Set<string>()
    for (const field of allFields(definition)) {
      if (field.optionSetId) ids.add(field.optionSetId)
    }
    return [...ids]
  }, [definition])

  // Fetch exactly the referenced sets; React Query dedupes across instances.
  const fetchedOptionSets = useOptionSetsByIds(referencedOptionSetIds)

  // MR fields: fetch the patient record only when the form actually has one and
  // a patient context exists. Preview (builder) never fetches — MR fields render
  // with an empty read-only placeholder there.
  const patientsEnabled = useFeatureFlag(FEATURES.patients)
  const hasMrFields = useMemo(
    () => allFields(definition).some(f => f.isMrField),
    [definition],
  )
  // Skip the fetch entirely when patients is off — it would only 403, and the
  // MR fields already render their own "unavailable" note.
  const patientQuery = usePatientDetail(
    submission.patientMrn || undefined,
    hasMrFields && !preview && patientsEnabled,
  )

  // Patient demographic value per MR key, display-formatted. Missing patient
  // (no MRN, 404, still loading) → empty strings, rendered as read-only blanks.
  const mrValues = useMemo<Record<MrFieldKey, string>>(() => {
    const p = patientQuery.data
    return {
      mrn: p?.mrn ?? '',
      firstName: p?.firstName ?? '',
      lastName: p?.lastName ?? '',
      fullName: p?.fullName ?? '',
      sex: p?.sex ?? '',
      // A calendar date (z8nrz7dp77): never `new Date(...)`, which reads it as UTC
      // midnight and shows the day before west of Greenwich.
      birthDate: formatCalendarDateShort(p?.birthDate),
      email: p?.email ?? '',
      phone: p?.phone ?? '',
      addressLine: p?.addressLine ?? '',
      city: p?.city ?? '',
      state: p?.state ?? '',
      postalCode: p?.postalCode ?? '',
      country: p?.country ?? '',
      preferredLanguage: p?.preferredLanguage ?? '',
    }
  }, [patientQuery.data])

  const optionSetLabels = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const set of fetchedOptionSets) {
      map.set(set.id, set.options.map(o => o.label))
    }
    return map
  }, [fetchedOptionSets])

  // Signature modal state — the shared SignatureDialog owns the signer name.
  // `required` is "Sign as:": the operator picks who signs (z8nrz7dt3z).
  const [signatureModal, setSignatureModal] = useState<
    { fieldId: string; mode: 'self' | 'required' } | null
  >(null)
  // "Remove Signature" / "Remove as:" (z8nrz7dt40) — signed and reasoned.
  const [removeModal, setRemoveModal] = useState<
    { fieldId: string; mode: 'self' | 'required' } | null
  >(null)
  const [removeReason, setRemoveReason] = useState('')
  const [removing, setRemoving] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const [signError, setSignError] = useState<string | null>(null)
  // "View Audit Log" / "View Allowed Signatories" on a signature field.
  const [auditField, setAuditField] = useState<{ id: string; label: string } | null>(null)
  const [allowedField, setAllowedField] = useState<{ id: string; label: string } | null>(null)
  // Open once completion has passed every check and is waiting on the signature.
  const [completionSigning, setCompletionSigning] = useState(false)

  useEffect(() => {
    setLocalSubmission(submission)
  }, [submission])

  // Signing a field locks what was filled for the rest of the session (see
  // setLockedFieldIds below). Unlocking the form reopens every field, and this
  // component is not remounted for it, so drop those locks on the way back
  // from 'completed'. A page reload gives the same state.
  const prevStatusRef = useRef(submission.status)
  useEffect(() => {
    if (
      (prevStatusRef.current === 'completed' || prevStatusRef.current === 'pending_review') &&
      localSubmission.status === 'active'
    ) {
      setLockedFieldIds(new Set())
    }
    prevStatusRef.current = localSubmission.status
  }, [localSubmission.status])

  // Merge authoritative server values over any local drafts whenever the
  // submission changes (keeps unsaved keystrokes, adopts computed results).
  useEffect(() => {
    setLiveValues(prev => ({ ...prev, ...localSubmission.values }))
  }, [localSubmission])

  // Cross-form values merge UNDER live values: `{handle.fieldId}` refs in
  // formulas and visibility conditions resolve from the namespaced keys, and
  // own-form keys always win (they can't collide — same-form ids are dot-free).
  // Live recompute falls out naturally: the host rebuilds `crossForm` whenever
  // sibling submissions refresh.
  //
  // recomputeCalculated resolves every calculated field (in dependency order)
  // into this map too, so a calculated field can reference ANOTHER calculated
  // field (e.g. "days between two calculated dates") and any formula gate can
  // reference a calculated field — all live, without waiting on a save
  // round-trip. See recomputeCalculated's docblock in lib/formula.ts.
  //
  // Calculated table cells are nodes in the same single pass as calculated
  // fields (z8nrz7eujb): a field may read a cell (`{rowId__colId}`) and a
  // cell may read a field, so neither order of two passes would be complete.
  // Table totals (`{tableId#col:colId:sum}`) are worked out first and merged
  // in under their token key: they count only number cells, which are
  // entered, never calculated, so no total waits on a formula. Same order as
  // cgt-backend's recompute. None of these keys is ever saved: a save names
  // one field or cell key, never this map.
  const evalValues = useMemo(() => {
    const merged = crossForm ? { ...crossForm.values, ...liveValues } : liveValues
    const fields = allFields(definition)
    return recomputeCalculated(
      [...fields, ...calculatedCellNodes(fields)],
      { ...merged, ...tableAggregateValues(fields, merged) },
      formulaCtx,
    )
  }, [crossForm, liveValues, definition, formulaCtx])

  // Field id → label, for rendering human-readable formula summaries.
  // Cross-form refs contribute "Label (Form Name)" entries, table cells and
  // totals theirs ("Vitals › Weight @ Day 1"); own fields win.
  const fieldLabels = useMemo(() => {
    const map: Record<string, string> = { ...crossForm?.labelById, ...tableRefLabels(allFields(definition)) }
    for (const f of allFields(definition)) map[f.id] = f.label
    return map
  }, [definition, crossForm])

  // Field id → fieldType, so the prerequisite hint can apply the right emptiness
  // check (e.g. a checkbox prerequisite is "filled" only when checked).
  const fieldTypeById = useMemo(() => {
    const map: Record<string, string> = {}
    for (const f of allFields(definition)) map[f.id] = f.fieldType
    return map
  }, [definition])

  // Conditional visibility: targets with a `visibility` rule render/hide based
  // on other fields' live values (distinct from the sequencing locks below —
  // hidden means NOT rendered, not disabled). Stored values are retained while
  // hidden so the target reappears with its data intact. A condition row may
  // itself be a raw Boolean formula (see VisibilityCondition.formula) — that's
  // handled inside computeHiddenSectionIds/computeHiddenFieldIds, which is why
  // formulaCtx (for TODAY()/NOW()) is threaded through here.
  const hiddenSectionIds = useMemo(
    () => computeHiddenSectionIds(getSections(definition), evalValues, formulaCtx),
    [definition, evalValues, formulaCtx],
  )
  const hiddenFieldIds = useMemo(
    () => computeHiddenFieldIds(getSections(definition), evalValues, formulaCtx),
    [definition, evalValues, formulaCtx],
  )

  // Intra-form sequencing: a field with `unlockedBy` prerequisite(s) stays locked
  // until ALL of them hold a non-empty value (AND). Recomputed on every liveValues
  // change so it unlocks live. Missing targets are treated gracefully (ignored),
  // and so are conditionally hidden ones — a hidden prerequisite can never be
  // filled, so letting it gate would deadlock the form.
  const prereqLockedIds = useMemo(() => {
    const fields = allFields(definition)
    const fieldById = new Map(fields.map(f => [f.id, f]))
    const locked = new Set<string>()
    for (const f of fields) {
      const prereqs = prerequisiteIds(f)
      if (prereqs.length === 0) continue
      // Locked while any existing, visible prerequisite is still empty.
      const anyUnfilled = prereqs.some(pid => {
        const prereq = fieldById.get(pid)
        if (!prereq) return false // target no longer exists → graceful
        if (hiddenFieldIds.has(pid)) return false // hidden → never gates
        // MR values live on the patient record, never in submission.values —
        // an MR prerequisite would read as forever-empty and deadlock.
        if (prereq.isMrField) return false
        return isEmptyValue(prereq.fieldType, evalValues[pid])
      })
      if (anyUnfilled) locked.add(f.id)
    }
    // A field's `unlockGate` (a raw Boolean formula) SUPERSEDES its
    // `unlockedBy` prerequisite list when set — same override pattern as
    // visibilityGate above. Fails closed: a blank/malformed gate stays locked.
    for (const f of fields) {
      if (!f.unlockGate?.formula) continue
      const unlocked = evaluateGateFormula(f.unlockGate.formula, evalValues, formulaCtx)
      if (unlocked) locked.delete(f.id)
      else locked.add(f.id)
    }
    return locked
  }, [definition, evalValues, hiddenFieldIds, formulaCtx])

  // Section-level sequencing: a section flagged `gatedByPrevious` stays locked
  // until the immediately-preceding section is complete (see isSectionComplete —
  // required fields if any, otherwise all fillable fields). Recomputed live so it
  // unlocks as the user fills fields.
  const lockedSectionIds = useMemo(
    () => computeLockedSectionIds(getSections(definition), evalValues, formulaCtx),
    [definition, evalValues, formulaCtx],
  )

  const handleLiveChange = useCallback((fieldId: string, value: unknown) => {
    setLiveValues(prev => ({ ...prev, [fieldId]: value }))
  }, [])

  /**
   * A process step transitioned, so re-read the submission from the server.
   *
   * The step's own state lives in its own query, but a transition can change
   * the SUBMISSION too: completing a kit run's step closes its form
   * (KitStepPolicyService.onCompleted). Without this the renderer kept its
   * stale `active` copy and went on offering "Complete Form" against a form
   * the API had already closed, which answered
   * "Cannot complete a form with status: completed".
   *
   * Skipped in preview, which never touches the API.
   */
  const handleProcessTransitioned = useCallback(() => {
    if (preview) return
    formsApi
      .getSubmission(localSubmission.id)
      .then(fresh => {
        setLocalSubmission(fresh)
        onSubmissionUpdated(fresh)
      })
      .catch(() => {
        // A failed refresh must not break the step that just succeeded; the
        // page's own reload still catches up.
      })
  }, [preview, localSubmission.id, onSubmissionUpdated])

  // Preview mode: merge a change into the submission locally, no API call.
  const applyLocal = useCallback(
    (values: Record<string, unknown>, extra?: Partial<FormSubmission>) => {
      const updated: FormSubmission = {
        ...localSubmission,
        values: { ...localSubmission.values, ...values },
        updatedAt: new Date().toISOString(),
        ...extra,
      }
      setLocalSubmission(updated)
      onSubmissionUpdated(updated)
      return updated
    },
    [localSubmission, onSubmissionUpdated],
  )

  // Auto-populate the MRN field from submission.patientMrn so the user
  // doesn't have to type it again after searching by MRN on the dashboard.
  useEffect(() => {
    if (preview) return
    if (!submission.patientMrn) return
    const mrnField = allFields(definition).find(
      f =>
        f.fieldType === 'text' &&
        (f.label.toLowerCase().includes('mrn') ||
          f.label.toLowerCase().includes('medical record')),
    )
    if (!mrnField) return
    if (submission.values[mrnField.id]) return // already has a value
    formsApi
      .saveFieldValue(submission.id, mrnField.id, submission.patientMrn)
      .then(({ submission: updated }) => {
        setLocalSubmission(updated)
        onSubmissionUpdated(updated)
      })
      .catch(console.error)
  // Intentionally runs only once on mount — submission.id / patientMrn won't change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // `pending_review` is closed too (z8nrz7dt41): the reviewer must sign
  // exactly what was completed.
  const isFormLocked = isSubmissionClosed(localSubmission.status)

  // Throws on failure. Most fields go through handleSaveField below, which
  // logs instead; a number field with a specification (z8nrz7eq4e) calls this
  // directly, so a refused save (no comment) can keep its prompt open.
  const saveField = useCallback(
    async (fieldId: string, newValue: unknown, comment?: string) => {
      const updated = preview
        ? applyLocal({ [fieldId]: newValue })
        : (await formsApi.saveFieldValue(localSubmission.id, fieldId, newValue, undefined, undefined, comment)).submission
      if (!preview) {
        setLocalSubmission(updated)
        onSubmissionUpdated(updated)
        // Every field change writes an audit row. This save does not go
        // through a TanStack mutation, so the cache-wide rule in
        // QueryProvider does not see it and an open trail panel would sit on
        // a copy that predates the edit.
        invalidateAuditTrail()
      }

      const field = allFields(definition).find(f => f.id === fieldId)
      if (field?.isGate && field.gatedFormType && newValue !== '' && newValue !== null && newValue !== undefined) {
        setGateBanners(prev =>
          prev.includes(field.gatedFormType!) ? prev : [...prev, field.gatedFormType!],
        )
      }
    },
    [preview, applyLocal, localSubmission.id, definition, onSubmissionUpdated, invalidateAuditTrail],
  )

  const handleSaveField = useCallback(
    async (fieldId: string, newValue: unknown, _oldValue: unknown, comment?: string) => {
      try {
        await saveField(fieldId, newValue, comment)
      } catch (e) {
        console.error('Failed to save field', fieldId, e)
      }
    },
    [saveField],
  )

  const handleSignatureAction = useCallback(
    (fieldId: string, action: SignatureAction) => {
      const field = allFields(definition).find(f => f.id === fieldId)
      const ref = { id: fieldId, label: field?.label ?? fieldId }
      setSignError(null)
      setRemoveError(null)
      setRemoveReason('')
      switch (action) {
        case 'apply':
          return setSignatureModal({ fieldId, mode: 'self' })
        case 'signAs':
          return setSignatureModal({ fieldId, mode: 'required' })
        case 'remove':
          return setRemoveModal({ fieldId, mode: 'self' })
        case 'removeAs':
          return setRemoveModal({ fieldId, mode: 'required' })
        case 'auditLog':
          return setAuditField(ref)
        case 'allowedSignatories':
          return setAllowedField(ref)
      }
    },
    [definition],
  )

  // Rosters for the pickers, fetched only while a picker is on screen.
  const signRoster = useSubmissionSigners(
    localSubmission.id,
    { action: 'sign', fieldId: signatureModal?.fieldId },
    !preview && signatureModal?.mode === 'required',
  )
  const removeRoster = useSubmissionSigners(
    localSubmission.id,
    { action: 'remove', fieldId: removeModal?.fieldId },
    !preview && removeModal?.mode === 'required',
  )
  const completeRoster = useSubmissionSigners(
    localSubmission.id,
    { action: 'complete' },
    !preview && completionSigning,
  )

  const confirmSignature = useCallback(async (signingToken: string, signer: ConfirmedSigner) => {
    if (!signatureModal) return
    const { fieldId } = signatureModal
    // What is sent is irrelevant to what is recorded: the server stamps the
    // signature from whoever's password minted the token and ignores this. It
    // only matters to the builder preview, which never reaches the server.
    const signatureValue = { signedBy: signer.name, signedAt: new Date().toISOString() }

    let updated: FormSubmission
    try {
      updated = preview
        ? applyLocal({ [fieldId]: signatureValue })
        : (
            await formsApi.saveFieldValue(
              localSubmission.id,
              fieldId,
              signatureValue,
              undefined,
              // Spent by the server before the signature is recorded: applying
              // one is an authenticated act, not a value like any other.
              signingToken,
            )
          ).submission
    } catch (e) {
      // A refused signature (outside the allowed groups, a spent token) stays
      // in the dialog, where the signer can read it and try again.
      setSignError(e instanceof Error ? e.message : 'The signature could not be applied.')
      return
    }
    if (!preview) {
      setLocalSubmission(updated)
      onSubmissionUpdated(updated)
      // A signature is an audited event like any other field change.
      invalidateAuditTrail()
    }

    const filledIds = allFields(definition)
      .filter(f => {
        // Table values are stored per-cell under composite keys, not on f.id.
        // Without this branch, signing a form left signed table cells editable.
        if (f.fieldType === 'table' && f.tableConfig) {
          const tc = f.tableConfig
          for (const row of tc.rows) {
            for (const col of tc.columns) {
              const v = updated.values[cellKey(row.id, col.id)]
              if (v !== undefined && v !== '' && v !== null) return true
            }
          }
          return false
        }
        const v = updated.values[f.id]
        return v !== undefined && v !== '' && v !== null
      })
      .map(f => f.id)
    setLockedFieldIds(prev => new Set([...prev, ...filledIds]))

    setSignatureModal(null)
  }, [
    signatureModal,
    preview,
    applyLocal,
    localSubmission.id,
    definition,
    onSubmissionUpdated,
    invalidateAuditTrail,
  ])

  // Remove Signature / Remove as: (z8nrz7dt40). The field goes back to
  // unsigned, and so does everything the signature locked in this session —
  // the server has already recomputed what was derived from it.
  const confirmRemoval = useCallback(
    async (signingToken: string) => {
      if (!removeModal) return
      const { fieldId } = removeModal
      setRemoving(true)
      setRemoveError(null)
      try {
        const updated = await formsApi.removeSignature(
          localSubmission.id,
          fieldId,
          signingToken,
          removeReason.trim(),
        )
        // The live-values merge only ever ADDS keys, so the removed signature
        // has to be dropped from it explicitly or the field keeps rendering it.
        setLiveValues(prev => {
          const next = { ...prev }
          delete next[fieldId]
          return next
        })
        setLocalSubmission(updated)
        onSubmissionUpdated(updated)
        invalidateAuditTrail()
        setLockedFieldIds(new Set())
        setRemoveModal(null)
      } catch (e) {
        setRemoveError(e instanceof Error ? e.message : 'The signature could not be removed.')
      } finally {
        setRemoving(false)
      }
    },
    [removeModal, localSubmission.id, removeReason, onSubmissionUpdated, invalidateAuditTrail],
  )

  // Removal needs the form open: a completed form is locked as a whole, and
  // unlocking it is its own signed act.
  const signatureRemoveBlocked =
    localSubmission.status === 'completed'
      ? 'The form is completed — unlock it first'
      : localSubmission.status === 'pending_review'
        ? 'The form is waiting for review — unlock it first'
      : localSubmission.status === 'cancelled'
        ? 'The form is cancelled'
        : null

  const handleComplete = useCallback(async () => {
    // Validate required fields against liveValues — the same state that drives
    // what the user sees (hiding, required asterisks), so the pre-submit check
    // never names an invisible field or passes one shown as required. The
    // backend re-runs this check on the persisted values and stays authoritative
    // if an in-flight save hasn't landed yet. Conditionally hidden fields/
    // sections are exempt: a hidden required field can't block completion. A
    // visibility rule with require:true makes its target required while visible.
    const errors: Record<string, string> = {}
    for (const field of allFields(definition)) {
      if (field.fieldType === 'section') continue // layout-only, no value
      if (field.fieldType === 'process') continue // state lives in ProcessStepExecution, not values
      if (valueIsAttachment(field.fieldType)) continue // the file IS the value — checked below
      if (isConstantField(field.fieldType)) continue // fixed text, nothing to fill in
      if (hiddenFieldIds.has(field.id)) continue
      if (!isFieldRequiredNow(field, evalValues, formulaCtx)) continue
      const v = evalValues[field.id]
      if (isEmptyValue(field.fieldType, v)) {
        errors[field.id] = 'This field is required.'
      }
    }
    // A required attachment blocks completion with the same weight as a required
    // value, and is surfaced through the SAME fieldErrors map so the filler sees
    // it in the place they already look for what's blocking them. In preview
    // mode there is no real submission for attachmentsByFieldId to ever contain
    // anything, so a picked-but-not-uploaded preview file (tracked separately —
    // see previewAttachments) satisfies the check instead.
    for (const field of allFields(definition)) {
      if (!field.attachment?.enabled || !field.attachment.required) continue
      if (hiddenFieldIds.has(field.id)) continue
      const hasAttachment = preview
        ? !!previewAttachments[field.id]
        : attachmentsByFieldId.has(field.id)
      if (hasAttachment) continue
      errors[field.id] = 'A file attachment is required.'
    }
    // An Image field is required the ordinary way — `field.required`, the
    // asterisk on its own label — but what satisfies it is a stored picture, not
    // an entry in the values map, so it is checked here rather than in the loop
    // above. Same live re-evaluation as every other field: a visibility rule can
    // make it required while shown.
    for (const field of allFields(definition)) {
      if (field.fieldType !== 'image') continue
      if (hiddenFieldIds.has(field.id)) continue
      if (!isFieldRequiredNow(field, evalValues, formulaCtx)) continue
      const hasImage = preview ? !!previewImages[field.id] : attachmentsByFieldId.has(field.id)
      if (hasImage) continue
      errors[field.id] = 'An image is required.'
    }
    // An Attachment field (z8nrz7dp7p) the same way: required means at least
    // one file on it. The server re-checks this on completion.
    for (const field of allFields(definition)) {
      if (!isMultiFileAttachment(field.fieldType)) continue
      if (hiddenFieldIds.has(field.id)) continue
      if (!isFieldRequiredNow(field, evalValues, formulaCtx)) continue
      const hasFile = preview
        ? (previewFileLists[field.id]?.length ?? 0) > 0
        : attachmentsByFieldId.has(field.id)
      if (hasFile) continue
      errors[field.id] = 'At least one file is required.'
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    setFieldErrors({})
    setCompletionError(null)
    // Whole-form completion gate — same fail-closed formula evaluation as
    // field-level gates. Re-checked server-side immediately before the
    // completion is accepted (this client-side check is UX only).
    if (definition.completionGate?.formula) {
      const ok = evaluateGateFormula(definition.completionGate.formula, evalValues, formulaCtx)
      if (!ok) {
        setCompletionError(
          definition.completionGate.message || 'This form cannot be completed right now.',
        )
        return
      }
    }
    // Host-supplied gate (e.g. "a product must be created first") — checked
    // last, after every built-in validation has already passed, so it never
    // masks a required-field or completion-gate error with its own message.
    if (beforeComplete && !beforeComplete()) return
    if (preview) {
      applyLocal({}, {
        status: 'completed',
        submittedBy: currentUser.name,
        submittedAt: new Date().toISOString(),
      })
      return
    }
    // Completing a form IS a signature — the server records `signedBy` and
    // writes a Part 11 audit row for it. It used to be applied silently from
    // the session, which is exactly the assertion-instead-of-authentication
    // this ticket exists to close. Every validation above has already passed,
    // so the only thing left is to sign.
    setCompletionSigning(true)
  }, [
    preview,
    applyLocal,
    definition,
    localSubmission,
    evalValues,
    hiddenFieldIds,
    attachmentsByFieldId,
    previewAttachments,
    previewImages,
    previewFileLists,
    currentUser.name,
    onSubmissionUpdated,
    formulaCtx,
    beforeComplete,
  ])

  const confirmCompletion = useCallback(
    async (signingToken: string) => {
      setCompleting(true)
      setCompletionError(null)
      try {
        const completed = await formsApi.completeSubmission(localSubmission.id, signingToken, completionExtras)
        setLocalSubmission(completed)
        onSubmissionUpdated(completed)
        // Completing writes its own audit row, and the panel showing it is on
        // this same screen. Another direct call, so another explicit refresh.
        invalidateAuditTrail()
        setCompletionSigning(false)
      } catch (e) {
        setCompletionError(e instanceof Error ? e.message : 'Failed to complete form.')
      } finally {
        setCompleting(false)
      }
    },
    [localSubmission.id, currentUser.name, onSubmissionUpdated, invalidateAuditTrail, completionExtras],
  )

  // Lets a host retry completion programmatically (e.g. "Skip" on its own gate
  // dialog) without a second Complete Form click. Only depends on
  // `completeTrigger` — NOT `handleComplete`, whose identity changes most
  // renders and would otherwise re-fire this on every unrelated re-render
  // (an infinite loop, since handleComplete's own validation setState calls
  // trigger exactly such a re-render). handleCompleteRef stays current via a
  // separate, unconditional effect instead.
  const handleCompleteRef = useRef(handleComplete)
  useEffect(() => {
    handleCompleteRef.current = handleComplete
  })
  const lastCompleteTrigger = useRef(completeTrigger)
  useEffect(() => {
    if (completeTrigger === undefined) return
    if (completeTrigger === lastCompleteTrigger.current) return
    lastCompleteTrigger.current = completeTrigger
    void handleCompleteRef.current()
  }, [completeTrigger])

  // Waiting for review reads as completed for everything on this screen — the
  // fields are dimmed and Complete Form is gone — but the form is not done;
  // the header status and the Review area say so. A skipped form is closed the
  // same way until someone unskips it.
  const isCompleted =
    localSubmission.status === 'completed' ||
    localSubmission.status === 'pending_review' ||
    localSubmission.status === 'skipped'

  // Render a single field node (table vs standard). `sectionLocked` disables the
  // field because its whole section is gated behind an earlier section.
  const renderFieldNode = (
    field: FieldDefinition,
    sectionLocked = false,
    placement: Pick<FieldPlacement, 'layout' | 'stacked'> = { layout: SYSTEM_LABEL_LAYOUT, stacked: false },
  ) => {
    // Conditionally hidden fields are not rendered at all (they reappear the
    // instant their visibility expression flips; values are retained).
    if (hiddenFieldIds.has(field.id)) return null
    // Review fields live in the Review area at the bottom, not in a section.
    if (field.fieldType === 'review') return null
    // A visibility rule with require:true makes the field required while shown —
    // reflect that on the label so the asterisk matches the completion check.
    if (!field.required && isFieldRequiredNow(field, evalValues, formulaCtx)) {
      field = { ...field, required: true }
    }
    const prereqLocked = prereqLockedIds.has(field.id)
    // Write-once (`canChange: false`): editable while empty, permanently
    // read-only once it holds a value. The backend re-checks the same rule in
    // saveFieldValue, so this disabled state is a courtesy, not the control.
    const writeLocked = isFieldWriteLocked(field, localSubmission.values[field.id])
    // Show the field-level sequencing hint only when the lock comes purely from
    // that field's own prerequisite — not when it's locked by completion, a
    // signature, or its section gate (the section shows its own banner).
    const showPrereqHint =
      prereqLocked && !isFormLocked && !lockedFieldIds.has(field.id) && !sectionLocked
    // Same rule for the write-once hint: only explain this lock when it is the
    // only thing stopping the edit, so we never stack two lock messages.
    const showWriteLockHint =
      writeLocked &&
      !isFormLocked &&
      !lockedFieldIds.has(field.id) &&
      !prereqLocked &&
      !sectionLocked
    const fieldLocked =
      isFormLocked || lockedFieldIds.has(field.id) || prereqLocked || sectionLocked || writeLocked
    const node =
      field.fieldType === 'image' ? (
        // Rendered here rather than inside RenderedField because everything it
        // needs — the attachment list, the upload/remove mutations, the per-field
        // busy/error state — is owned by this component, exactly as the
        // attachment control beside every other field type is.
        <ImageField
          submissionId={localSubmission.id}
          fieldId={field.id}
          label={field.label}
          helpText={field.helpText}
          attachment={attachmentsByFieldId.get(field.id)}
          required={field.required}
          locked={fieldLocked}
          busy={attachmentFieldState[field.id]?.busy ?? false}
          error={
            attachmentFieldState[field.id]?.error ??
            (attachmentsByFieldId.has(field.id) ? undefined : fieldErrors[field.id])
          }
          preview={preview}
          previewImage={previewImages[field.id]}
          onPreviewImageChange={image => setPreviewImage(field.id, image)}
          layout={placement.layout}
          stacked={placement.stacked}
          {...attachmentSlotHandlers(field.id, attachments, setAttachmentFieldState)}
        />
      ) : isMultiFileAttachment(field.fieldType) ? (
        <AttachmentField
          submissionId={localSubmission.id}
          fieldId={field.id}
          label={field.label}
          helpText={field.helpText}
          files={attachments.filesByFieldId.get(field.id) ?? []}
          required={isFieldRequiredNow(field, evalValues, formulaCtx)}
          locked={fieldLocked}
          busy={attachmentFieldState[field.id]?.busy ?? false}
          error={
            attachmentFieldState[field.id]?.error ??
            (attachmentsByFieldId.has(field.id) ? undefined : fieldErrors[field.id])
          }
          preview={preview}
          previewFiles={previewFileLists[field.id]}
          onPreviewFilesChange={files =>
            setPreviewFileLists(prev => ({ ...prev, [field.id]: files }))
          }
          layout={placement.layout}
          stacked={placement.stacked}
          {...attachmentListHandlers(field.id, attachments, setAttachmentFieldState)}
        />
      ) : field.fieldType === 'table' ? (
        <>
        <InlineTableRenderer
          field={field}
          allValues={evalValues}
          locked={fieldLocked}
          formulaCtx={formulaCtx}
          crossForm={crossForm}
          onSave={handleSaveField}
          onSaveStrict={saveField}
          onLiveChange={handleLiveChange}
          savedValues={localSubmission.values}
          submissionId={localSubmission.id}
          preview={preview}
          receivedTestCodes={receivedTestCodes}
          renderImageCell={(key, caption, cellLocked) => (
            <TableImageCell
              submissionId={localSubmission.id}
              cellKey={key}
              caption={caption}
              attachment={attachmentsByFieldId.get(key)}
              locked={cellLocked}
              busy={attachmentFieldState[key]?.busy ?? false}
              error={attachmentFieldState[key]?.error}
              preview={preview}
              previewImage={previewImages[key]}
              onPreviewImageChange={image => setPreviewImage(key, image)}
              {...attachmentSlotHandlers(key, attachments, setAttachmentFieldState)}
            />
          )}
        />
        {fieldErrors[field.id] && (
          <p role='alert' className='mt-1 text-xs text-destructive'>
            {fieldErrors[field.id]}
          </p>
        )}
        </>
      ) : (
        <RenderedField
          field={field}
          value={localSubmission.values[field.id]}
          locked={isFormLocked || lockedFieldIds.has(field.id) || prereqLocked || sectionLocked || writeLocked}
          error={fieldErrors[field.id]}
          currentUser={currentUser}
          allValues={evalValues}
          fieldLabels={fieldLabels}
          resolvedOptions={
            field.optionSetId ? (optionSetLabels.get(field.optionSetId) ?? []) : undefined
          }
          mrValue={
            field.isMrField && field.mrFieldKey ? mrValues[field.mrFieldKey] : undefined
          }
          submissionId={localSubmission.id}
          preview={preview}
          formulaCtx={formulaCtx}
          onSave={handleSaveField}
          onSaveStrict={saveField}
          onLiveChange={handleLiveChange}
          onSignatureAction={handleSignatureAction}
          signatureRemoveBlocked={signatureRemoveBlocked}
          signatureSetPeers={
            field.fieldType === 'signature' ? distinctSetPeers(allFields(definition), field) : []
          }
          onProcessTransitioned={handleProcessTransitioned}
          layout={placement.layout}
          stacked={placement.stacked}
        />
      )
    // Per-field attachment control, under the field it belongs to. Rendered for
    // any field whose definition opted in, regardless of field type.
    const attachmentNode = field.attachment?.enabled ? (
      <AttachmentControl
        submissionId={localSubmission.id}
        fieldId={field.id}
        fieldLabel={field.label}
        attachment={attachmentsByFieldId.get(field.id)}
        required={field.attachment.required}
        locked={isFormLocked || lockedFieldIds.has(field.id) || prereqLocked || sectionLocked || writeLocked}
        busy={attachmentFieldState[field.id]?.busy ?? false}
        // Priority: a mutation failure on THIS field beats everything else — it's
        // the freshest, most specific signal. Otherwise fall back to the stale
        // Complete-click snapshot, but only while the field is STILL actually
        // missing its attachment (re-checked live) — `fieldErrors` is never
        // cleared incrementally, so without this re-check the message could
        // keep saying "required" after the icon has already turned green.
        error={
          attachmentFieldState[field.id]?.error ??
          (attachmentsByFieldId.has(field.id) ? undefined : fieldErrors[field.id])
        }
        preview={preview}
        previewFile={previewAttachments[field.id]}
        onPreviewFileChange={file =>
          setPreviewAttachments(prev => {
            const next = { ...prev }
            if (file) next[field.id] = file
            else delete next[field.id]
            return next
          })
        }
        {...attachmentSlotHandlers(field.id, attachments, setAttachmentFieldState)}
      />
    ) : null

    // Cross-form reference notes: a subtle "waiting" hint while the upstream
    // form is unfilled, a hard warning when the referenced form/field no longer
    // resolves — never a silently wrong or stale value.
    const fieldCrossRefs = [
      ...(field.formula ? extractFormulaRefs(field.formula) : []),
      ...visibilitySourceIds(field.visibility),
    ]
    const crossNotes = crossForm ? crossFormNotesFor(fieldCrossRefs, crossForm.stateByRef) : []
    // The attachment icon sits BESIDE its field, not under it, so a form with
    // several attachable fields reads as a column of fields with a status rail
    // rather than a stack of upload widgets.
    //
    // Completion-dimming is scoped to the FIELD half only, not the icon: an
    // attached file's icon manages its own locked/attached appearance inside
    // AttachmentControl (green + clickable-to-view even when locked; only the
    // empty/no-file state stays inert, since there's nothing to view and no new
    // upload should be allowed post-completion). Wrapping BOTH halves in one
    // dimmed ancestor — the previous approach — defeated that: CSS opacity
    // composites its whole subtree and pointer-events-none blocks every
    // descendant's clicks, so the icon LOOKED and BEHAVED disabled regardless
    // of what AttachmentControl itself decided.
    //
    // A field that SHOWS something rather than holding an input is carved out of
    // the dimming for the same reason the icon is. An Image field, and a table
    // carrying an image column, exist to be looked at: `opacity-60` fades the
    // photograph itself (opacity composites its whole subtree, so nothing
    // inside can opt back in) and `pointer-events-none` swallows the click that
    // opens it full size — leaving a completed form showing a washed-out
    // picture nobody can enlarge. Both already render their own locked state:
    // the picker is disabled and Replace/Remove are gone, so there is nothing
    // left for the dimming to protect.
    //
    // A field with a specification (z8nrz7eq4e, z8nrz7eq4h) is carved out
    // too, and so is a table with one on any column or cell: the flag opens
    // the alert review, which a group member may do after the form is
    // completed. The inputs are already disabled through `locked`.
    const fieldDim =
      isCompleted && !showsImagery(field) && !hasOosSpec(field)
        ? 'opacity-60 pointer-events-none'
        : undefined
    const fieldRow = attachmentNode ? (
      <div className='flex items-center gap-2'>
        <div className={cn('min-w-0 flex-1', fieldDim)}>{node}</div>
        {attachmentNode}
      </div>
    ) : fieldDim ? (
      <div className={fieldDim}>{node}</div>
    ) : (
      node
    )

    // The test code the field is connected to, or its value was received with
    // (z8nrz7g2wm), as a label beneath it. A label only: the integration's
    // mapping decides what is written here. A table shows its codes per cell, on hover.
    const shownCode =
      field.fieldType === 'table'
        ? null
        : shownTestCode(field.lisTestCode, receivedTestCodes.get(field.id))
    const withAttachment = shownCode ? (
      <div>
        {fieldRow}
        <p
          className='mt-1 text-xs text-muted-foreground'
          title={testCodeText(shownCode)}
          data-lis-test-code={shownCode.code}
        >
          Test code <span className='font-mono font-medium text-foreground/80'>{shownCode.code}</span>
          {shownCode.received ? ' · received from LIS' : null}
        </p>
      </div>
    ) : (
      fieldRow
    )

    if (!showPrereqHint && !showWriteLockHint && crossNotes.length === 0) return withAttachment
    // Name the prerequisite(s) still unfilled (AND — all must be filled to unlock).
    const pending = prerequisiteIds(field)
      .filter(pid => isEmptyValue(fieldTypeById[pid] ?? 'text', evalValues[pid]))
      .map(pid => fieldLabels[pid])
      .filter(Boolean)
    const pendingLabel = pending.length
      ? pending.map(l => `'${l}'`).join(', ')
      : 'the previous field(s)'
    return (
      <div className='space-y-1.5'>
        {withAttachment}
        {showPrereqHint && (
          <p className='flex items-center gap-1 text-xs text-muted-foreground'>
            <Lock className='h-3 w-3 shrink-0' />
            Fill {pendingLabel} first.
          </p>
        )}
        {showWriteLockHint && (
          <p className='flex items-center gap-1 text-xs text-muted-foreground'>
            <Lock className='h-3 w-3 shrink-0' />
            This field cannot be changed once it has a value.
          </p>
        )}
        {crossNotes.map(note => (
          <p
            key={note.text}
            className={cn(
              'flex items-center gap-1 text-xs',
              note.tone === 'warning' ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {note.tone === 'warning' ? (
              <AlertCircle className='h-3 w-3 shrink-0' />
            ) : (
              <Lock className='h-3 w-3 shrink-0' />
            )}
            {note.text}
          </p>
        ))}
      </div>
    )
  }

  // Sections pack into a 12-col grid by width and wrap; fields stack vertically
  // within their section. Literal class strings so Tailwind's JIT keeps them.
  const sections = getSections(definition)

  function renderFieldLayout() {
    // `isCompleted` no longer dims/blocks THIS wrapper — it's applied per-field
    // instead, scoped to just each field's own control (see withAttachment
    // below), so a field's attachment icon can stay outside the lockout.
    return (
      <div className='grid grid-cols-1 items-start gap-5 sm:grid-cols-12'>
        {sections.map((section, i) => {
          // A conditionally hidden section is not rendered at all — it appears
          // the instant its visibility expression flips.
          if (hiddenSectionIds.has(section.id)) return null
          const sectionLocked = lockedSectionIds.has(section.id)
          // Prerequisite section titles for the locked banner — explicit
          // unlockedBySections when set, else the legacy immediately-preceding
          // section. Only existing, strictly-earlier sections are shown.
          const explicitPrereqs = sectionPrerequisiteIds(section)
          const prereqIds =
            explicitPrereqs.length > 0
              ? explicitPrereqs
              : section.gatedByPrevious && i > 0
                ? [sections[i - 1].id]
                : []
          const prereqLabels = prereqIds
            .map(id => sections.findIndex(s => s.id === id))
            .filter(idx => idx >= 0 && idx < i)
            .map(idx => sections[idx].title || `Section ${idx + 1}`)
          return (
            <section
              key={section.id}
              className={cn(
                'overflow-hidden rounded-lg border bg-card shadow-sm transition-opacity',
                SECTION_SPAN_CLASS[section.width],
                sectionLocked && 'opacity-60',
              )}
              aria-disabled={sectionLocked || undefined}
            >
              {section.title && (
                <div
                  className={cn(
                    'flex items-center gap-2 border-b px-4 py-2.5 sm:px-5',
                    section.headerColor && section.headerColor !== 'none'
                      ? SECTION_COLOR_HEADER_CLASS[section.headerColor]
                      : 'bg-muted/30',
                  )}
                >
                  {sectionLocked && <Lock className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />}
                  <h3 className='text-sm font-semibold leading-tight'>{section.title}</h3>
                </div>
              )}
              {sectionLocked && (
                <div className='flex items-center gap-1.5 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 sm:px-5'>
                  <Lock className='h-3 w-3 shrink-0' />
                  Complete{' '}
                  {prereqLabels.length > 0
                    ? prereqLabels.map(l => `"${l}"`).join(' and ')
                    : 'the required section(s)'}{' '}
                  to unlock this section.
                </div>
              )}
              <SectionFieldGrid
                fields={section.fields}
                formLayout={definition.fieldLayout}
                optionSetLabels={optionSetLabels}
                locked={sectionLocked}
                renderField={(field, placement) => renderFieldNode(field, sectionLocked, placement)}
              />
            </section>
          )
        })}
      </div>
    )
  }

  return (
    <ProcessRunBatchContext.Provider value={processBatchContext}>
      <div className={hideHeader ? 'space-y-4' : 'space-y-6'}>
        {gateBanners.map(ft => (
            <div
              key={ft}
              className='rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-center justify-between gap-3'
            >
              <div className='flex items-center gap-2'>
                <Check className='h-4 w-4 text-blue-600 shrink-0' />
                <p className='text-sm text-blue-800'>
                  Gate condition met —{' '}
                  <span className='font-semibold'>{CGT_FORM_LABELS[ft]}</span> is now available for{' '}
                  <span className='font-mono'>{localSubmission.patientMrn}</span>.
                </p>
              </div>
              <button
                onClick={() => setGateBanners(prev => prev.filter(b => b !== ft))}
                className='text-blue-500 hover:text-blue-700'
              >
                <X className='h-4 w-4' />
              </button>
            </div>
          ))}

        {/* Form header */}
        {!hideHeader && (
        <div className='border-b pb-4'>
          <h2 className='text-lg font-semibold'>{definition.name}</h2>
          <p className='text-xs text-muted-foreground mt-1'>
            {recordLabel}:{' '}
            <span className='font-mono font-medium'>{localSubmission.patientMrn}</span>
            {' · '}Status:{' '}
            <span
              className={cn(
                'font-medium',
                localSubmission.status === 'completed'
                  ? 'text-green-700'
                  : localSubmission.status === 'draft' || localSubmission.status === 'pending_review'
                    ? 'text-amber-700'
                    : 'text-foreground',
              )}
            >
              {submissionStatusLabel(localSubmission.status)}
            </span>
          </p>
        </div>
        )}


        {/* Fields */}
        {renderFieldLayout()}

        {/* Complete button */}
        {!isCompleted && !hideComplete && (
          <div className='pt-4 border-t flex flex-col items-end gap-2'>
            {completionError && (
              <div className='flex items-center gap-2 text-sm text-destructive'>
                <AlertCircle className='h-4 w-4' />
                {completionError}
              </div>
            )}
            {Object.keys(fieldErrors).length > 0 && (
              <p className='text-xs text-destructive'>
                Please fill all required fields before completing.
              </p>
            )}
            {/* Completing locks the submission, and a locked submission cannot
                accept a step transition — so doing this mid-run would strand the
                step permanently, and on a kit run the kit could then never be
                produced, because production happens at End. The server refuses
                it and says so; this component deliberately does not fetch the
                executions to pre-empt that, because it has no tenant context and
                acquiring one to grey out a button is the wrong trade. */}
            <Button onClick={handleComplete} disabled={completing}>
              {completing ? (
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
              ) : (
                <Check className='h-4 w-4 mr-2' />
              )}
              Complete Form
            </Button>
          </div>
        )}

        {/* Not in the builder preview: that renders against a throwaway
            submission that was never persisted, so the trail would be a request
            for a record that cannot exist. Same rule as `useAttachments` above —
            preview renders fully, but never hits the API. */}
        {/* Review fields (z8nrz7dt41): one grey area at the bottom, never in
            their sections. Assignable while the form is filled; signed by the
            assignee once it is completed. */}
        <ReviewArea
          definition={definition}
          submission={localSubmission}
          currentUser={currentUser}
          preview={preview}
          onSubmissionUpdated={updated => {
            setLocalSubmission(updated)
            onSubmissionUpdated(updated)
          }}
        />

        {!preview && <AuditTrailPanel submissionId={localSubmission.id} />}

        {/* Signature modal — Apply Signature, or "Sign as:" with a picker */}
        <SignatureDialog
          open={!!signatureModal}
          onOpenChange={open => !open && setSignatureModal(null)}
          title={signatureModal?.mode === 'required' ? 'Sign as' : 'Apply Signature'}
          description={
            signatureModal?.mode === 'required'
              ? 'Choose who is signing. They enter their own password; the signature is recorded as theirs, and this workstation stays signed in as you.'
              : 'Enter your password to apply an electronic signature. This action will lock all currently filled fields.'
          }
          confirmLabel='Confirm Signature'
          signerName={currentUser.name}
          error={signError}
          signAs={
            signatureModal?.mode === 'required'
              ? {
                  mode: 'required',
                  options: signRoster.data,
                  loading: signRoster.isLoading,
                  error: signRoster.isError ? 'Could not load the list of signers.' : null,
                  selfId: currentUser.id || null,
                }
              : undefined
          }
          onConfirm={(token, signer) => void confirmSignature(token, signer)}
        />

        {/* Remove Signature / Remove as: — reasoned, and signed by the remover */}
        <SignatureDialog
          open={!!removeModal}
          onOpenChange={open => !open && !removing && setRemoveModal(null)}
          title={removeModal?.mode === 'required' ? 'Remove as' : 'Remove Signature'}
          description='The signature is removed from this field and the field can be signed again. The original signature stays on the audit trail.'
          confirmLabel='Remove & Sign'
          signerName={currentUser.name}
          busy={removing}
          error={removeError}
          confirmDisabled={removeReason.trim() === ''}
          signAs={
            removeModal?.mode === 'required'
              ? {
                  mode: 'required',
                  label: 'Remove as',
                  options: removeRoster.data,
                  loading: removeRoster.isLoading,
                  error: removeRoster.isError ? 'Could not load the list of people.' : null,
                  selfId: currentUser.id || null,
                }
              : undefined
          }
          onConfirm={token => void confirmRemoval(token)}
        >
          <div className='space-y-1.5'>
            <Label htmlFor='remove-signature-reason' className='text-xs'>
              Reason for removing the signature (required)
            </Label>
            <Textarea
              id='remove-signature-reason'
              value={removeReason}
              onChange={e => setRemoveReason(e.target.value)}
              placeholder='e.g. Signed the wrong field'
              rows={2}
              maxLength={2000}
            />
          </div>
        </SignatureDialog>

        {/* Completion signature — the form is valid and waiting to be signed off.
            Defaults to the signed-in user; a colleague may sign it off instead. */}
        <SignatureDialog
          open={completionSigning}
          onOpenChange={open => !open && setCompletionSigning(false)}
          title='Complete this form'
          description='Enter your password to sign off and lock this form.'
          confirmLabel='Sign & Complete'
          signerName={currentUser.name}
          busy={completing}
          error={completionError}
          signAs={
            preview
              ? undefined
              : {
                  mode: 'optional',
                  options: completeRoster.data,
                  loading: completeRoster.isLoading,
                  error: completeRoster.isError ? 'Could not load the list of signers.' : null,
                  selfId: currentUser.id || null,
                }
          }
          onConfirm={token => void confirmCompletion(token)}
        />

        {!preview && (
          <>
            <FieldAuditLogDialog
              submissionId={localSubmission.id}
              field={auditField}
              onClose={() => setAuditField(null)}
            />
            <AllowedSignatoriesDialog
              submissionId={localSubmission.id}
              field={allowedField}
              onClose={() => setAllowedField(null)}
            />
          </>
        )}
      </div>
    </ProcessRunBatchContext.Provider>
  )
}
